import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { inject, observer } from 'mobx-react'
import { Layout, Button, Row, Col, DatePicker, Form, Checkbox, Input, Select, Switch, Tooltip } from 'antd'
import moment from 'moment'
import {
    CATEGORIES,
    DEFAULT_TERMINALS,
    STATUS_HEX,
    categoryColor,
    initialTransactionDateRange
} from '../utils'

// TODO: swap labels to Hebrew (אשראי / בנק) if the ops team prefers
const TERMINALS = DEFAULT_TERMINALS.map(value => ({ value, label: value }))
const CARD_TERMINALS = ['mediwho1', 'mediwho1tok']
const BANK_TERMINALS = ['mediwhobk']
const CLIENT_SEARCH_PARAMETERS = [
    {
        key: 'client_id',
        label: 'מזהה לקוח',
        placeholder: 'לדוגמה: 109852',
        terminals: DEFAULT_TERMINALS
    },
    {
        key: 'email',
        label: 'אימייל',
        placeholder: 'לדוגמה: name@example.com',
        terminals: DEFAULT_TERMINALS
    },
    {
        key: 'phone',
        label: 'טלפון',
        placeholder: 'לדוגמה: 0541234567',
        terminals: DEFAULT_TERMINALS
    },
    { key: 'token', label: 'טוקן', placeholder: 'טוקן מלא', terminals: CARD_TERMINALS },
    { key: 'last4', label: '4 ספרות אחרונות', placeholder: 'לדוגמה: 1234', terminals: CARD_TERMINALS },
    {
        key: 'owner_id',
        label: 'ת"ז בעל הכרטיס',
        placeholder: 'תעודת זהות של בעל הכרטיס',
        terminals: CARD_TERMINALS
    },
    { key: 'bank', label: 'בנק', placeholder: 'קוד בנק', terminals: BANK_TERMINALS },
    { key: 'bank_branch', label: 'סניף', placeholder: 'מספר סניף', terminals: BANK_TERMINALS },
    { key: 'bank_account', label: 'חשבון', placeholder: 'מספר חשבון', terminals: BANK_TERMINALS }
]
const initialClientSearchDateRange = () => ({
    clientFdate: moment('2021-03-31', 'YYYY-MM-DD'),
    clientTdate: moment()
})

// antd computes breakpoints from the viewport, although the work area is ~200px narrower due to the sidebar.
// On xl (including 1280/1366) browse: 2 + 6 + 6 + 10 = 24;
// client search row 1: 2 + 5 + 5 + 5 + 7 = 24; row 2: category + terminals/actions = 24.
// Terminals and actions live inside a single Col: this way the buttons sit next to the control group,
// not at the edge of an empty grid span.
// 2 spans are enough for the toggle: its content is a label (~73px) and a Switch (44px), while 3 spans gave
// 114px, and the extra ~70px of empty grid span read as an enlarged gap before the adjacent date.
// The freed span goes to the neighbor in the row, so the total stays 24.
const COL_QUERY_MODE = { xs: 24, sm: 24, md: 24, lg: 24, xl: 2, xxl: 2 }
const COL_QUERY_DATE = { xs: 24, sm: 12, md: 12, lg: 12, xl: 5, xxl: 5 }
const COL_BROWSE_DATE = { xs: 24, sm: 12, md: 12, lg: 12, xl: 6, xxl: 6 }
const COL_QUERY_CATEGORY = { xs: 24, sm: 24, md: 24, lg: 24, xl: 10, xxl: 10 }
const COL_SEARCH_FIELD = { xs: 24, sm: 12, md: 12, lg: 12, xl: 5, xxl: 5 }
const COL_SEARCH_VALUE = { xs: 24, sm: 12, md: 12, lg: 12, xl: 7, xxl: 7 }
const COL_QUERY_TERMINAL = { xs: 24, sm: 24, md: 24, lg: 24, xl: 24, xxl: 24 }
const COL_SEARCH_CATEGORY = { xs: 24, sm: 24, md: 24, lg: 24, xl: 8, xxl: 8 }
const COL_SEARCH_TERMINAL_ACTIONS = { xs: 24, sm: 24, md: 24, lg: 24, xl: 16, xxl: 16 }

// Balance each wrapped row instead of leaving a lone 6-span field. At xl (1280/1366 viewport,
// sidebar included) 2 -> 12+12, 3 -> 8+8+8, 5 -> 8+8+8 / 12+12,
// 6 -> 8+8+8 / 8+8+8, and 9 -> three rows of 8+8+8.
const balancedSpan = (index, count, maxPerRow) => {
    const rowCount = Math.ceil(count / maxPerRow)
    const baseSize = Math.floor(count / rowCount)
    const largerRows = count % rowCount
    let offset = 0
    for (let row = 0; row < rowCount; row += 1) {
        const rowSize = baseSize + (row < largerRows ? 1 : 0)
        if (index < offset + rowSize) return 24 / rowSize
        offset += rowSize
    }
    return 24
}
const filterColProps = (index, count) => ({
    xs: 24,
    sm: balancedSpan(index, count, 2),
    md: balancedSpan(index, count, 2),
    lg: balancedSpan(index, count, 4),
    xl: balancedSpan(index, count, 4),
    xxl: balancedSpan(index, count, 4)
})
// gutter in antd 3 is horizontal only: when columns wrap there will be no vertical gap
const ROW = { type: 'flex' as 'flex', gutter: 16, align: 'bottom' as 'bottom', style: { rowGap: '0.8rem' } }
const LABEL_STYLE = {
    display: 'block',
    marginBottom: '0.35rem',
    fontSize: '0.85rem',
    lineHeight: '1.2rem',
    whiteSpace: 'nowrap' as 'nowrap'
}
const QUERY_CONTROL_STYLE = {
    display: 'flex',
    alignItems: 'flex-end',
    minHeight: 34,
    width: '100%',
    lineHeight: 'normal'
}
const MODE_CONTROL_STYLE = {
    ...QUERY_CONTROL_STYLE,
    // The Switch is 44px and the grid cannot hand out anything narrower than a span, so its Col
    // still has slack. Pin the Switch to the edge that faces the rest of the row (flex-end is the
    // left edge under direction: rtl) — then the visible spacing is Row.gutter alone, 16px, the
    // same as every other control here. Without this the leftover span sits between the Switch
    // and the next control and the gap measures 42px instead of 16px.
    justifyContent: 'flex-start'
}

@inject((store: any) => {
    const { doctorsStore, specializationsStore, companiesStore, regionsStore, popupStore } = store
    return { doctorsStore, specializationsStore, companiesStore, regionsStore, popupStore }
})
@observer
class SearchBar extends Component<any, any> {
    state = {
        Fdate: this.props.defaultFdate ? this.props.defaultFdate.clone() : moment().subtract(1, 'day'),
        Tdate: this.props.defaultTdate ? this.props.defaultTdate.clone() : moment(),
        terminals: [...(this.props.defaultTerminals || DEFAULT_TERMINALS)],
        clientParam: undefined,
        clientValue: '',
        ...initialClientSearchDateRange()
    }

    handleFromChange = m => {
        if (!m) return
        const { Tdate } = this.state
        // picking a start after the end pulls the end along with it
        this.setState({ Fdate: m, Tdate: Tdate && m.isAfter(Tdate, 'day') ? m.clone() : Tdate })
        this.props.resetPage()
    }

    handleToChange = m => {
        if (!m) return
        const { Fdate } = this.state
        // picking an end before the start pulls the start back
        this.setState({ Tdate: m, Fdate: Fdate && m.isBefore(Fdate, 'day') ? m.clone() : Fdate })
        this.props.resetPage()
    }

    handleTerminalsChange = terminals => {
        this.setState({ terminals })
        this.props.setTerminals(terminals)
    }

    handleClientFromChange = clientFdate => {
        const { clientTdate } = this.state
        this.setState({
            clientFdate,
            clientTdate:
                clientFdate && clientTdate && clientFdate.isAfter(clientTdate, 'day')
                    ? clientFdate.clone()
                    : clientTdate
        })
        this.props.clearClientSearchResults()
    }

    handleClientToChange = clientTdate => {
        const { clientFdate } = this.state
        this.setState({
            clientTdate,
            clientFdate:
                clientTdate && clientFdate && clientTdate.isBefore(clientFdate, 'day')
                    ? clientTdate.clone()
                    : clientFdate
        })
        this.props.clearClientSearchResults()
    }

    handleModeChange = clientSearchMode => {
        const browseDates = initialTransactionDateRange()
        this.setState({
            ...browseDates,
            terminals: [...DEFAULT_TERMINALS],
            clientParam: undefined,
            clientValue: '',
            ...initialClientSearchDateRange()
        })
        this.props.setClientSearchMode(clientSearchMode)
    }

    render() {
        const {
            handleSubmit,
            handleReset,
            filters,
            setFilter,
            counts,
            billingCounts,
            responseTotal,
            categories,
            setCategories,
            errorCodes,
            errorOptions,
            setErrorCodes,
            handleExport,
            exportRowsCount,
            exportLoading,
            filterDefinitions,
            clientSearchMode,
            handleClientSearch,
            clearClientSearchResults
        } = this.props
        const { Fdate, Tdate, terminals, clientParam, clientValue, clientFdate, clientTdate } = this.state
        const selectedClientParameter = CLIENT_SEARCH_PARAMETERS.find(parameter => parameter.key === clientParam)
        const normalizedClientValue = clientValue.trim()
        const validatedClientValue =
            clientParam === 'phone'
                ? normalizedClientValue
                      .replace(/\D/g, '')
                      .replace(/^972/, '')
                      .replace(/^0/, '')
                : normalizedClientValue
        const clientSearchDisabled = !selectedClientParameter || validatedClientValue.length < 3
        const exportFdate = clientSearchMode ? clientFdate : Fdate
        const exportTdate = clientSearchMode ? clientTdate : Tdate
        // New billing categories use the precise API aggregates; old ones stay on group counts.
        const categoryCount = c => {
            const billingKeys = c.billingCountKeys || []
            const hasBillingCount =
                billingKeys.length > 0 &&
                billingKeys.every(key => billingCounts && Object.prototype.hasOwnProperty.call(billingCounts, key))
            if (hasBillingCount) {
                return billingKeys.reduce((sum, key) => sum + Number(billingCounts[key] || 0), 0)
            }
            return c.groups
                ? c.groups.reduce((sum, group) => sum + Number((counts && counts[group]) || 0), 0)
                : Number(responseTotal || 0)
        }
        // block only the future; range inversion is fixed by the auto-adjust in onChange.
        // endOf('day') is required: current is the start of the day, moment() carries the current time
        const disabledFutureDate = current => !!current && current > moment().endOf('day')
        const exportDisabled = !exportRowsCount
        const exportButton = (
            <Button
                htmlType="button"
                loading={exportLoading}
                disabled={exportLoading || exportDisabled}
                style={{ marginLeft: 0, pointerEvents: exportDisabled ? 'none' : 'auto' }}
                onClick={() =>
                    handleExport({
                        from: exportFdate ? exportFdate.format('YYYY-MM-DD') : null,
                        to: exportTdate ? exportTdate.format('YYYY-MM-DD') : null,
                        terminals: clientSearchMode
                            ? [...(selectedClientParameter ? selectedClientParameter.terminals : [])]
                            : [...terminals],
                        categories: [...categories],
                        errorCodes: [...errorCodes]
                    })
                }
            >
                ייצוא לאקסל
            </Button>
        )
        const categorySelect = (
            <Select
                className="tranzila-category-select"
                mode="multiple"
                style={{ width: '100%' }}
                dropdownMatchSelectWidth={false}
                value={categories}
                placeholder="הכל"
                optionLabelProp="children"
                onChange={setCategories}
            >
                {CATEGORIES.map(c => (
                    <Select.Option key={c.key} value={c.key}>
                        <span>
                            <span style={{ color: categoryColor(c) }}>{c.label}</span>
                            <span>{` (${categoryCount(c)})`}</span>
                        </span>
                    </Select.Option>
                ))}
            </Select>
        )
        const errorSelect = (
            <Select
                className="tranzila-category-select"
                mode="multiple"
                style={{ width: '100%' }}
                value={errorCodes}
                placeholder="הכל"
                // Col here is 10 spans (~110px in search): with dropdownMatchSelectWidth the list inherits
                // the control's width and cuts off the label together with the count at the end of the line.
                // false switches rc-select from width to minWidth — the menu grows with its content.
                dropdownMatchSelectWidth={false}
                onChange={setErrorCodes}
            >
                {errorOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                        {`${option.label} (${option.count})`}
                    </Select.Option>
                ))}
            </Select>
        )
        const categoryAndErrorSelects = (
            <Row type="flex" gutter={16} align="bottom">
                <Col span={14}>
                    <strong style={LABEL_STYLE}>קטגוריה</strong>
                    <div style={QUERY_CONTROL_STYLE}>{categorySelect}</div>
                </Col>
                <Col span={10}>
                    <strong style={LABEL_STYLE}>שגיאה</strong>
                    <div style={QUERY_CONTROL_STYLE}>{errorSelect}</div>
                </Col>
            </Row>
        )
        return (
            <Form
                className="tranzila-search-bar"
                onSubmit={e => {
                    e.preventDefault()
                    if (clientSearchMode) {
                        if (clientSearchDisabled) return
                        handleClientSearch({
                            param: selectedClientParameter.key,
                            label: selectedClientParameter.label,
                            value: normalizedClientValue,
                            from: clientFdate ? clientFdate.format('YYYY-MM-DD') : null,
                            to: clientTdate ? clientTdate.format('YYYY-MM-DD') : null,
                            terminals: [...selectedClientParameter.terminals]
                        })
                        return
                    }
                    handleSubmit(
                        Fdate ? Fdate.format('YYYY-MM-DD') : null,
                        Tdate ? Tdate.format('YYYY-MM-DD') : null,
                        terminals
                    )
                }}
            >
                <Layout style={{ padding: '0 1rem' }}>
                    <div
                        style={{
                            padding: '0.75rem',
                            border: '1px solid #e8e8e8',
                            borderRadius: 4,
                            background: '#fafafa'
                        }}
                    >
                        <Row {...ROW}>
                            <Col {...COL_QUERY_MODE}>
                                <strong style={LABEL_STYLE}>חיפוש ממוקד</strong>
                                <div style={MODE_CONTROL_STYLE}>
                                    <Switch checked={clientSearchMode} onChange={this.handleModeChange} />
                                </div>
                            </Col>
                            {clientSearchMode ? (
                                <React.Fragment>
                                    <Col {...COL_QUERY_DATE}>
                                        <strong style={LABEL_STYLE}>מהתאריך (אופציונלי)</strong>
                                        <DatePicker
                                            value={clientFdate}
                                            format="DD/MM/YYYY"
                                            allowClear={false}
                                            style={{ width: '100%', border: 'none' }}
                                            disabledDate={disabledFutureDate}
                                            onChange={this.handleClientFromChange}
                                        />
                                    </Col>
                                    <Col {...COL_QUERY_DATE}>
                                        <strong style={LABEL_STYLE}>עד לתאריך (אופציונלי)</strong>
                                        <DatePicker
                                            value={clientTdate}
                                            format="DD/MM/YYYY"
                                            allowClear={false}
                                            style={{ width: '100%', border: 'none' }}
                                            disabledDate={disabledFutureDate}
                                            onChange={this.handleClientToChange}
                                        />
                                    </Col>
                                    <Col {...COL_SEARCH_FIELD}>
                                        <strong style={LABEL_STYLE}>פרמטר חיפוש</strong>
                                        <Select
                                            style={{ width: '100%' }}
                                            value={clientParam}
                                            placeholder="בחר פרמטר"
                                            onChange={value => {
                                                this.setState({ clientParam: value, clientValue: '' })
                                                clearClientSearchResults()
                                            }}
                                        >
                                            {CLIENT_SEARCH_PARAMETERS.map(parameter => (
                                                <Select.Option key={parameter.key} value={parameter.key}>
                                                    {parameter.label}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Col>
                                    <Col {...COL_SEARCH_VALUE}>
                                        <strong style={LABEL_STYLE}>ערך</strong>
                                        <Input
                                            allowClear
                                            dir="ltr"
                                            value={clientValue}
                                            placeholder={
                                                selectedClientParameter
                                                    ? selectedClientParameter.placeholder
                                                    : 'בחר תחילה פרמטר'
                                            }
                                            onChange={e => {
                                                this.setState({ clientValue: e.target.value })
                                                clearClientSearchResults()
                                            }}
                                        />
                                    </Col>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <Col {...COL_BROWSE_DATE}>
                                        <strong style={LABEL_STYLE}>מהתאריך</strong>
                                        <DatePicker
                                            value={Fdate}
                                            format="DD/MM/YYYY"
                                            allowClear={false}
                                            style={{ width: '100%', border: 'none' }}
                                            disabledDate={disabledFutureDate}
                                            onChange={this.handleFromChange}
                                        />
                                    </Col>
                                    <Col {...COL_BROWSE_DATE}>
                                        <strong style={LABEL_STYLE}>עד לתאריך</strong>
                                        <DatePicker
                                            value={Tdate}
                                            format="DD/MM/YYYY"
                                            allowClear={false}
                                            style={{ width: '100%', border: 'none' }}
                                            disabledDate={disabledFutureDate}
                                            onChange={this.handleToChange}
                                        />
                                    </Col>
                                    <Col {...COL_QUERY_CATEGORY}>
                                        {categoryAndErrorSelects}
                                    </Col>
                                </React.Fragment>
                            )}
                        </Row>
                        <Row {...ROW} style={{ ...ROW.style, marginTop: '0.8rem' }}>
                            {clientSearchMode && (
                                <Col {...COL_SEARCH_CATEGORY}>
                                    {categoryAndErrorSelects}
                                </Col>
                            )}
                            <Col {...(clientSearchMode ? COL_SEARCH_TERMINAL_ACTIONS : COL_QUERY_TERMINAL)}>
                                <strong style={LABEL_STYLE}>
                                    {clientSearchMode ? 'טרמינלים שייכללו בחיפוש (לקריאה בלבד)' : 'טרמינל'}
                                </strong>
                                <div
                                    className="tranzila-terminal-actions"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        flexWrap: 'wrap',
                                        justifyContent: 'flex-start'
                                    }}
                                >
                                    <div style={{ whiteSpace: 'nowrap' }}>
                                        {clientSearchMode ? (
                                            <span
                                                dir="ltr"
                                                style={{
                                                    display: 'inline-flex',
                                                    minHeight: 32,
                                                    alignItems: 'center',
                                                    padding: '0 0.75rem',
                                                    border: '1px solid #d9d9d9',
                                                    borderRadius: 4,
                                                    background: '#f5f5f5'
                                                }}
                                            >
                                                {selectedClientParameter
                                                    ? selectedClientParameter.terminals.join(' · ')
                                                    : 'בחר פרמטר כדי לראות את הטרמינלים'}
                                            </span>
                                        ) : (
                                            <React.Fragment>
                                                <Checkbox.Group
                                                    style={{ whiteSpace: 'nowrap' }}
                                                    value={terminals}
                                                    onChange={this.handleTerminalsChange}
                                                >
                                                    {TERMINALS.map(t => (
                                                        <Checkbox key={t.value} value={t.value}>
                                                            {t.label}
                                                        </Checkbox>
                                                    ))}
                                                </Checkbox.Group>
                                                {!terminals.length && (
                                                    <span style={{ color: STATUS_HEX.red, marginRight: '0.75rem' }}>
                                                        יש לבחור לפחות טרמינל אחד
                                                    </span>
                                                )}
                                            </React.Fragment>
                                        )}
                                    </div>
                                    <div
                                        className="tranzila-form-actions"
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'nowrap',
                                            marginRight: '1rem'
                                        }}
                                    >
                                        <Button
                                            type="primary"
                                            style={{ marginLeft: '0.5rem' }}
                                            htmlType="submit"
                                            disabled={clientSearchMode ? clientSearchDisabled : !terminals.length}
                                        >
                                            חפש
                                        </Button>
                                        <Button
                                            htmlType="button"
                                            style={{ marginLeft: '0.5rem' }}
                                            onClick={() => {
                                                if (clientSearchMode) {
                                                    this.setState({
                                                        clientParam: undefined,
                                                        clientValue: '',
                                                        ...initialClientSearchDateRange()
                                                    })
                                                    clearClientSearchResults(true)
                                                    return
                                                }
                                                const dateRange = initialTransactionDateRange()
                                                this.setState({ ...dateRange, terminals: [...DEFAULT_TERMINALS] })
                                                handleReset(dateRange.Fdate, dateRange.Tdate)
                                            }}
                                        >
                                            איפוס
                                        </Button>
                                        {exportDisabled ? (
                                            <Tooltip title="אין נתונים לייצוא">
                                                <span style={{ display: 'inline-block' }}>{exportButton}</span>
                                            </Tooltip>
                                        ) : (
                                            exportButton
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                    {/* Definitions and their disabled flags are snapshotted from the raw response, never from `shown`. */}
                    {!!filterDefinitions.length && (
                        <Row {...ROW} style={{ ...ROW.style, marginTop: '0.8rem' }}>
                            {filterDefinitions.map((filter, index) => {
                                const input = (
                                    <Input
                                        allowClear
                                        disabled={filter.disabled}
                                        dir={filter.dir}
                                        style={{ width: '100%', pointerEvents: filter.disabled ? 'none' : 'auto' }}
                                        value={filters[filter.key] || ''}
                                        onChange={e => setFilter(filter.key, e.target.value)}
                                    />
                                )
                                return (
                                    <Col {...filterColProps(index, filterDefinitions.length)} key={filter.key}>
                                        <strong style={LABEL_STYLE}>{filter.label}</strong>
                                        <div>
                                            {filter.disabled ? (
                                                <Tooltip title="אין נתונים בתוצאות הנוכחיות">
                                                    <span
                                                        style={{
                                                            display: 'block',
                                                            cursor: 'not-allowed',
                                                            opacity: 0.7
                                                        }}
                                                    >
                                                        {input}
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                input
                                            )}
                                        </div>
                                    </Col>
                                )
                            })}
                        </Row>
                    )}
                </Layout>
            </Form>
        )
    }
}

export default withRouter(SearchBar)
